import {
  LayoutDashboard,
  Rss,
  ClipboardList,
  Package,
  User,
  Users,
  LogOut,
  Sun,
  Moon,
  UserCircle,
  ChevronRight,
  RefreshCw,
  Stethoscope,
  Mail,
  Home,
  AlertTriangle,
  Bell,
  Crown,
  Building2,
  Settings as SettingsIcon,
  ShieldCheck,
  Activity,
  Download,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { usePatientRole } from '@/hooks/usePatientRole';
import { useShift } from '@/hooks/useShift';
import { useCompany } from '@/hooks/useCompany';
import { useHasRole } from '@/hooks/useHasRole';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from './LanguageSwitcher';
import { SupportMenu } from '@/components/support/SupportMenu';
import { ThemedLogo } from '@/components/ui/themed-logo';
import { AppStoreButtons } from '@/components/mobile/AppStoreButtons';
import { Capacitor } from '@capacitor/core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function AppSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, subscription, isLoggingOut } = useAuth();
  const { focusedPatient, setFocusedPatientId } = useFocusedPatient();
  const { canViewDashboard, isCaregiver, isLoading: isRoleLoading } = usePatientRole(focusedPatient?.id);
  const { activeShift } = useShift();
  const { isCompanyManager, isCompanyManagerOrCoordinator, isCompanyMember, isLoading: isCompanyLoading } = useCompany();
  const isDpo = useHasRole('security_officer');
  const isCareosAdmin = useHasRole('careos_admin');

  const shiftInOtherPatient = activeShift && focusedPatient && activeShift.patient_id !== focusedPatient.id;
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { state } = useSidebar();
  const [mounted, setMounted] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const isCollapsed = state === 'collapsed';

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: inventoryAlerts = [] } = useQuery({
    queryKey: ['inventory-alerts', focusedPatient?.id],
    queryFn: async () => {
      if (!focusedPatient?.id) return [];
      const { data, error } = await supabase
        .from('inventory')
        .select('id')
        .eq('patient_id', focusedPatient.id)
        .neq('status', 'ok');
      if (error) throw error;
      return data || [];
    },
    enabled: !!focusedPatient?.id
  });

  const handleLogout = async () => {
    if (isLoggingOut) return;
    await signOut();
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const currentTheme = mounted ? resolvedTheme : 'light';

  const patientMenuItems = focusedPatient && !isRoleLoading ? [
    {
      icon: LayoutDashboard,
      label: t('nav.panel'),
      path: `/app/patients/${focusedPatient.id}`,
      badge: 0
    },
    ...(canViewDashboard ? [
      {
        icon: Rss,
        label: t('nav.followPatient'),
        path: `/app/family/${focusedPatient.id}`,
        badge: 0
      },
    ] : []),
    {
      icon: ClipboardList,
      label: t('carePlan.title'),
      path: `/app/patients/${focusedPatient.id}/care-plan`,
      badge: 0
    },
    {
      icon: Package,
      label: t('nav.inventory'),
      path: `/app/inventory/${focusedPatient.id}`,
      badge: inventoryAlerts.length
    },
  ] : [];

  return (
    <Sidebar collapsible="icon" className="border-r border-border pt-[env(safe-area-inset-top)]" data-tour="app-navigation">
      <div className="p-4 flex items-center gap-3">
        <ThemedLogo className="h-8 w-8 object-contain shrink-0" />
        {!isCollapsed && (
          <span className="font-semibold text-lg">Care - OS</span>
        )}
      </div>

      {/* User Info with Avatar - top position (unified with mobile) */}
      {user && !isCollapsed && (
        <div className="px-3 pb-3 flex items-center gap-3">
          <SignedAvatar
            src={profile?.avatar_url}
            bucket="avatars"
            className="h-10 w-10"
            fallback={
              <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                {profile?.full_name?.charAt(0) || user.email?.charAt(0)?.toUpperCase() || '?'}
              </span>
            }
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {profile?.full_name || user.email}
            </p>
            {profile?.full_name && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                <Mail className="h-3 w-3 shrink-0" />
                {user.email}
              </p>
            )}
          </div>
        </div>
      )}
      <SidebarSeparator />

      <SidebarContent>
        {/* Home Link */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/app')}
                  tooltip={t('nav.home')}
                  className={cn(
                    "w-full",
                    location.pathname === '/app' && "bg-primary/10 text-primary"
                  )}
                >
                  <Home className="h-5 w-5" />
                  <span>{t('nav.home')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Company Dashboard - For managers and coordinators */}
              {isCompanyManagerOrCoordinator && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/app/company')}
                    tooltip={t('company.title')}
                    className={cn(
                      "w-full",
                      location.pathname === '/app/company' && "bg-primary/10 text-primary"
                    )}
                  >
                    <Building2 className="h-5 w-5" />
                    <span>{t('company.title')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* Company Settings - Only for managers */}
              {isCompanyManager && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/app/company/settings')}
                    tooltip={t('company.settingsTitle')}
                    className={cn(
                      "w-full pl-8",
                      location.pathname === '/app/company/settings' && "bg-primary/10 text-primary"
                    )}
                  >
                    <SettingsIcon className="h-4 w-4" />
                    <span className="text-sm">{t('company.settingsTitle')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* DPO Panel — visible only for users with security_officer role.
                  Backend RLS on data_subject_requests / events / attachments and
                  the manage-dpo-role edge function enforce real access control;
                  this UI gate just hides the entry point for non-DPOs. */}
              {isDpo === true && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/app/dpo')}
                    tooltip={t('nav.dpoPanelTooltip')}
                    className={cn(
                      "w-full",
                      location.pathname === '/app/dpo' && "bg-primary/10 text-primary"
                    )}
                  >
                    <ShieldCheck className="h-5 w-5" />
                    <span>{t('nav.dpoPanel')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {/* CareOS Admin — internal staff only */}
              {isCareosAdmin === true && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate('/app/admin')}
                      tooltip={t('nav.adminPanel')}
                      className={cn(
                        "w-full",
                        location.pathname.startsWith('/app/admin') && "bg-primary/10 text-primary"
                      )}
                    >
                      <Activity className="h-5 w-5" />
                      <span>{t('nav.adminPanel')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Focused Patient Section */}
        <SidebarGroup>
          <SidebarGroupLabel>
            {isCollapsed ? <UserCircle className="h-4 w-4" /> : t('nav.focusedPatient')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {focusedPatient ? (
                <>
                  {/* Patient Info */}
                  <SidebarMenuItem>
                    <div className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md bg-primary/5 mb-1",
                      isCollapsed && "justify-center px-0"
                    )}>
                      <SignedAvatar
                        src={focusedPatient.photo_url}
                        bucket="patient-photos"
                        className="h-8 w-8"
                        fallback={
                          <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                            {focusedPatient.nickname.charAt(0).toUpperCase()}
                          </span>
                        }
                      />
                      {!isCollapsed && (
                        <span className="font-medium text-sm truncate">
                          {focusedPatient.nickname}
                        </span>
                      )}
                    </div>
                  </SidebarMenuItem>

                  {/* Change Patient Link */}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate('/app/patients')}
                      tooltip={t('nav.changePatient')}
                      className="w-full text-xs text-muted-foreground hover:text-foreground mb-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>{t('nav.changePatient')}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  {/* Patient Menu Items */}
                  {patientMenuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;

                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.path)}
                          tooltip={item.label}
                          className={cn(
                            "w-full relative",
                            isActive && "bg-primary/10 text-primary"
                          )}
                        >
                          <Icon className="h-5 w-5" />
                          <span className="flex-1">{item.label}</span>
                          {item.badge > 0 && (
                            <Badge variant="destructive" className="h-5 min-w-5 px-1 flex items-center justify-center text-[12px]">
                              {item.badge}
                            </Badge>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}

                  {/* Caregiver Mode - Only for caregivers, shown after role loads */}
                  {!isRoleLoading && isCaregiver && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate(`/app/caregiver/${focusedPatient.id}`)}
                        tooltip={t('nav.caregiverMode')}
                        className={cn(
                          "w-full",
                          location.pathname.startsWith(`/app/caregiver/${focusedPatient.id}`) && "bg-primary/10 text-primary"
                        )}
                      >
                        <Stethoscope className="h-5 w-5" />
                        <span>{t('nav.caregiverMode')}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </>
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/app/patients')}
                    tooltip={t('nav.choosePatient')}
                    className="w-full text-muted-foreground"
                  >
                    <UserCircle className="h-5 w-5" />
                    <span>{t('nav.choosePatient')}</span>
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Active shift in other patient alert */}
        {shiftInOtherPatient && (
          <SidebarGroup>
            <SidebarGroupContent>
              <button
                onClick={() => {
                  setFocusedPatientId(activeShift.patient_id);
                  navigate(`/app/caregiver/${activeShift.patient_id}`);
                }}
                className={cn(
                  "w-full flex items-center gap-2 rounded-md bg-warning/15 text-warning border border-warning/30 p-3 text-sm font-medium hover:bg-warning/25 transition-colors",
                  isCollapsed && "justify-center p-2"
                )}
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {!isCollapsed && (
                  <div className="text-left">
                    <p className="text-xs leading-tight">{t('shift.activeElsewhereBanner')}</p>
                    <p className="text-xs underline mt-0.5">{t('shift.tapToSwitch')}</p>
                  </div>
                )}
              </button>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarSeparator />

        {/* Account Section */}
        <SidebarGroup>
          <SidebarGroupLabel>
            {isCollapsed ? <User className="h-4 w-4" /> : t('nav.account')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/app/profile')}
                  tooltip={t('nav.myProfile')}
                  className={cn(
                    "w-full",
                    location.pathname === '/app/profile' && "bg-primary/10 text-primary"
                  )}
                >
                  <User className="h-5 w-5" />
                  <span>{t('nav.myProfile')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/app/patients')}
                  tooltip={t('nav.myPatients')}
                  className={cn(
                    "w-full",
                    location.pathname === '/app/patients' && "bg-primary/10 text-primary"
                  )}
                >
                  <Users className="h-5 w-5" />
                  <span>{t('nav.myPatients')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/app/settings/notifications')}
                  tooltip={t('settings.notifications')}
                  className={cn(
                    "w-full",
                    location.pathname === '/app/settings/notifications' && "bg-primary/10 text-primary"
                  )}
                >
                  <Bell className="h-5 w-5" />
                  <span>{t('settings.notifications')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Subscription Link */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/app/subscription')}
                  tooltip={t('subscription.title')}
                  className={cn(
                    "w-full",
                    location.pathname === '/app/subscription' && "bg-primary/10 text-primary"
                  )}
                >
                  <Crown className={cn("h-5 w-5", subscription.subscribed && "text-primary")} />
                  <span className="flex-1">{t('subscription.title')}</span>
                  {subscription.subscribed && (
                    <Badge variant="default" className="h-5 min-w-5 px-1.5 text-[12px]">
                      {t('subscription.activeShort')}
                    </Badge>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Create Company - Only for non-members */}
              {!isCompanyLoading && !isCompanyMember && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/app/company/new')}
                    tooltip={t('company.createCompany')}
                    className={cn(
                      "w-full",
                      location.pathname === '/app/company/new' && "bg-primary/10 text-primary"
                    )}
                  >
                    <Building2 className="h-5 w-5" />
                    <span>{t('company.createCompany')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {!Capacitor.isNativePlatform() && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => setDownloadDialogOpen(true)}
                    tooltip={t('appDownload.menuItem')}
                    className="w-full"
                  >
                    <Download className="h-5 w-5" />
                    <span>{t('appDownload.menuItem')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem>
                <SupportMenu variant="sidebar" />
              </SidebarMenuItem>

              <SidebarMenuItem>
                <LanguageSwitcher variant="sidebar" />
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={toggleTheme}
                  tooltip={currentTheme === 'dark' ? t('settings.lightTheme') : t('settings.darkTheme')}
                  className="w-full"
                >
                  {currentTheme === 'dark' ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                  <span>{currentTheme === 'dark' ? t('settings.lightTheme') : t('settings.darkTheme')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              disabled={isLoggingOut}
              tooltip={t('auth.logout')}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className={cn("h-5 w-5", isLoggingOut && "animate-spin")} />
              <span>{isLoggingOut ? t('common.loading') : t('auth.logoutAccount')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('appDownload.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('appDownload.storeDialogDescription')}</DialogDescription>
          </DialogHeader>
          <AppStoreButtons />
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}